// Local tracker store (Dexie / IndexedDB), one database per signed-in user so a shared phone
// never mixes families. Entries are kept in the server shape plus a little sync bookkeeping.
import Dexie, { type Table } from 'dexie';
import type { Entry, EntryType } from '@babyplan/core';

/** What the server returns for an entry (docs/api-families-tracker.md). */
export type ServerEntry = Entry & {
  rev: number;
  createdById: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type LocalEntry = ServerEntry & {
  /** 1 = a local change waits in the queue. */
  dirty: 0 | 1;
  /** Local version: bumped on every local write, so a push response never overwrites a newer local edit. */
  lv: number;
  /** Last version confirmed by the server (null = never synced). Restored when the server rejects a change. */
  base: ServerEntry | null;
  /** 1 = was included in a push at least once (the server may know it even if we never saw the reply). */
  sent: 0 | 1;
  /** 1 = no end and not deleted (a running timer or a point-in-time entry); absent otherwise. Indexed: running timers are found without a scan. */
  run?: 1;
};

const isOpen = (e: { endAt?: string | null; deletedAt?: string | null }) => e.endAt === null && !e.deletedAt;

export type QueueItem = {
  /** entry id: one queue item per entry, always pushing the entry's latest state */
  id: string;
  babyId: string;
  op: 'upsert' | 'delete';
  /** first time this entry was queued (ms), for offline_queue_flush.oldest_sec */
  queuedAt: number;
  /** not pushed before this time (deletes wait for the undo toast) */
  notBefore: number;
  /** the queued op a delete replaced, so undo can put it back */
  prevOp?: 'upsert' | null;
};

export type MetaRow = { key: string; value: unknown };

export type EntryInput = {
  type: EntryType;
  startAt: string;
  endAt?: string | null;
  data: Record<string, unknown>;
  note?: string | null;
  categoryId?: string | null;
};

class TrackDB extends Dexie {
  entries!: Table<LocalEntry, string>;
  queue!: Table<QueueItem, string>;
  meta!: Table<MetaRow, string>;
  constructor(name: string) {
    super(name);
    this.version(1).stores({
      entries: 'id, babyId, [babyId+startAt]',
      queue: 'id, babyId',
      meta: 'key',
    });
    this.version(2)
      .stores({ entries: 'id, babyId, [babyId+startAt], [babyId+run]' })
      .upgrade((tx) => tx.table('entries').toCollection().modify((e: LocalEntry) => { if (isOpen(e)) e.run = 1; else delete e.run; }));
    // keep `run` in step with endAt / deletedAt on every write
    this.entries.hook('creating', (_key, obj) => { if (isOpen(obj)) obj.run = 1; else delete obj.run; });
    this.entries.hook('updating', (mods, _key, obj) => {
      const m = mods as Partial<LocalEntry>;
      const next = { endAt: 'endAt' in m ? m.endAt : obj.endAt, deletedAt: 'deletedAt' in m ? m.deletedAt : obj.deletedAt };
      return { run: isOpen(next) ? 1 : undefined };
    });
  }
}

let current: { userId: string; db: TrackDB } | null = null;

/** Open (or switch to) the store of this user. Safe to call on every render. */
export function openStore(userId: string): TrackDB {
  if (current?.userId === userId) return current.db;
  current?.db.close();
  current = { userId, db: new TrackDB(`bp-track-${userId}`) };
  return current.db;
}

export function store(): TrackDB {
  if (!current) throw new Error('track store is not open (call openStore(userId) first)');
  return current.db;
}

export const storeUserId = () => current?.userId ?? null;

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await store().meta.get(key))?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown) {
  await store().meta.put({ key, value });
}

/** Wipe this user's local tracker data (call on sign-out). */
export async function clearStore(userId: string) {
  if (current?.userId === userId) { current.db.close(); current = null; }
  await Dexie.delete(`bp-track-${userId}`);
}
